import { styled, useTheme } from "@enfusion-ui/web-core";
import { ColumnRowGroupChangedEvent, GridOptions } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React from "react";

import DirectionCellRenderer from "./DirectionCellRenderer";
import PercentageCellRenderer from "./PercentageCellRenderer";

export type DataGridProps = {
  height: string | number | undefined;
  width?: string | number | undefined;
  id?: string;
  ignoreColumns?: boolean;
  gridName?: string;
  onColumnStateChanged?: () => void;
  onColumnRowGroupChanged?: (event: ColumnRowGroupChangedEvent) => void;
  isWidget?: boolean;
  rowHeight?: number;
  hide?: boolean;
  className?: string;
} & GridOptions;

const DataGrid = React.forwardRef<HTMLDivElement, DataGridProps>(
  (
    {
      height,
      rowHeight = 28,
      width = "100%",
      ignoreColumns = false,
      columnDefs,
      onGridReady,
      onColumnStateChanged,
      onColumnRowGroupChanged,
      onSortChanged,
      isWidget,
      groupDisplayType = "groupRows",
      hide = false,
      id,
      className,
      ...rest
    },
    ref
  ) => {
    const { theme } = useTheme();

    return (
      <div
        ref={ref}
        className={
          isWidget
            ? className
            : `${className || ""} ${theme.agGridClass}`.trim()
        }
        style={{ height, width, display: hide ? "none" : "inherit" }}
        id={id}
      >
        {(ignoreColumns || !!columnDefs) && (
          <AgGridReact
            {...rest}
            rowHeight={rowHeight}
            groupDisplayType={groupDisplayType}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            onColumnResized={onColumnStateChanged}
            onDragStopped={onColumnStateChanged}
            onColumnPinned={onColumnStateChanged}
            onColumnVisible={onColumnStateChanged}
            onColumnMoved={onColumnStateChanged}
            onColumnRowGroupChanged={(event) => {
              onColumnStateChanged?.();
              onColumnRowGroupChanged?.(event);
            }}
            onSortChanged={(event) => {
              onColumnStateChanged?.();
              onSortChanged?.(event);
            }}
            components={{
              directionCellRenderer: DirectionCellRenderer,
              percentageCellRenderer: PercentageCellRenderer,
            }}
          />
        )}
      </div>
    );
  }
);

export const RoundedDataGrid = styled(DataGrid)`
  > div {
    width: 100%;
  }

  .ag-root-wrapper {
    border-width: 0px !important;
    background-color: transparent;
  }

  .ag-root-wrapper-body.ag-layout-normal.ag-focus-managed {
    border: solid 1px;
    border-color: #424242;
    border-color: var(--ag-border-color, #424242);
    border-radius: var(--radius);
    background-color: var(--ag-background-color);
  }

  .ag-root-wrapper .ag-column-drop-wrapper .ag-column-drop-horizontal {
    border-bottom-width: 0px;
    background-color: transparent !important;
  }

  .ag-header {
    border-top-left-radius: calc(var(--radius) + 1.5px);
    border-top-right-radius: calc(var(--radius) + 1.5px);
  }

  .ag-center-cols-container {
    min-width: 100% !important;
  }
`;

export default DataGrid;
